import type { EntityCrossFilter, EntityGraph, PropagatedConstraints } from './types';
import { campaignKeyForAdGroup, portfolioIdForCampaign } from './buildEntityGraph';
import { normalizeCrossFilter } from './toggleFilter';

/** Entity-hierarchy pins only (excludes attribute / facet dimensions). */
export function isEntityPinFilterEmpty(filter: EntityCrossFilter): boolean {
  const f = normalizeCrossFilter(filter);
  return (
    f.portfolioIds.length === 0 &&
    f.campaignKeys.length === 0 &&
    f.adGroupIds.length === 0 &&
    f.keywordIds.length === 0
  );
}

function intersect<T>(left: Set<T>, right: Set<T>): Set<T> {
  const out = new Set<T>();
  for (const value of left) {
    if (right.has(value)) out.add(value);
  }
  return out;
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function campaignKeysInPortfolios(portfolioIds: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const pid of portfolioIds) {
    for (const campaign of graph.indices.campaignsByPortfolioId.get(pid) ?? []) {
      out.add(campaign.campaignKey);
    }
  }
  return out;
}

function adGroupIdsInCampaigns(campaignKeys: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const ck of campaignKeys) {
    for (const ag of graph.indices.adGroupsByCampaignKey.get(ck) ?? []) {
      out.add(ag.adGroupId);
    }
  }
  return out;
}

function keywordIdsForParents(
  adGroupIds: Set<string>,
  campaignKeys: Set<string>,
  graph: EntityGraph
): Set<string> {
  const out = new Set<string>();
  for (const pins of graph.indices.keywordPinsByGroupKey.values()) {
    for (const pin of pins) {
      if (!adGroupIds.has(pin.adGroupId)) continue;
      const ck = campaignKeyForAdGroup(pin.adProduct, pin.campaignId);
      if (!campaignKeys.has(ck)) continue;
      out.add(pin.keywordId);
    }
  }
  return out;
}

function adGroupIdsForKeywords(keywordIds: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const pins of graph.indices.keywordPinsByGroupKey.values()) {
    for (const pin of pins) {
      if (keywordIds.has(pin.keywordId) && pin.adGroupId) out.add(pin.adGroupId);
    }
  }
  return out;
}

function campaignKeysForAdGroups(adGroupIds: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const agId of adGroupIds) {
    const ag = graph.indices.adGroupsById.get(agId);
    if (!ag) continue;
    out.add(campaignKeyForAdGroup(ag.adProduct, ag.campaignId));
  }
  return out;
}

function campaignKeysForKeywords(keywordIds: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const pins of graph.indices.keywordPinsByGroupKey.values()) {
    for (const pin of pins) {
      if (keywordIds.has(pin.keywordId)) {
        out.add(campaignKeyForAdGroup(pin.adProduct, pin.campaignId));
      }
    }
  }
  return out;
}

function portfolioIdsForCampaigns(campaignKeys: Set<string>, graph: EntityGraph): Set<string> {
  const out = new Set<string>();
  for (const ck of campaignKeys) {
    const campaign = graph.indices.campaignsByKey.get(ck);
    if (!campaign) continue;
    out.add(portfolioIdForCampaign(campaign));
  }
  return out;
}

export function propagateEntityConstraints(
  graph: EntityGraph,
  filter: EntityCrossFilter
): PropagatedConstraints {
  const f = normalizeCrossFilter(filter);
  const allP = new Set(graph.portfolios.map((p) => p.portfolioId));
  const allC = new Set(graph.campaigns.map((c) => c.campaignKey));
  const allA = new Set(graph.adGroups.map((a) => a.adGroupId));
  const allK = new Set(graph.indices.allKeywordIds);

  if (isEntityPinFilterEmpty(f)) {
    return {
      portfolioIds: allP,
      campaignKeys: allC,
      adGroupIds: allA,
      keywordIds: allK,
      conflict: false,
    };
  }

  let P = f.portfolioIds.length ? new Set(f.portfolioIds) : new Set(allP);
  let C = f.campaignKeys.length ? new Set(f.campaignKeys) : new Set(allC);
  let A = f.adGroupIds.length ? new Set(f.adGroupIds) : new Set(allA);
  let K = f.keywordIds.length ? new Set(f.keywordIds) : new Set(allK);

  for (let i = 0; i < 8; i += 1) {
    const prevP = new Set(P);
    const prevC = new Set(C);
    const prevA = new Set(A);
    const prevK = new Set(K);

    C = intersect(C, campaignKeysInPortfolios(P, graph));
    A = intersect(A, adGroupIdsInCampaigns(C, graph));
    K = intersect(K, keywordIdsForParents(A, C, graph));

    if (f.keywordIds.length) {
      A = intersect(A, adGroupIdsForKeywords(K, graph));
      C = intersect(C, campaignKeysForAdGroups(A, graph));
      C = intersect(C, campaignKeysForKeywords(K, graph));
      P = intersect(P, portfolioIdsForCampaigns(C, graph));
    } else if (f.adGroupIds.length) {
      C = intersect(C, campaignKeysForAdGroups(A, graph));
      P = intersect(P, portfolioIdsForCampaigns(C, graph));
    } else if (f.campaignKeys.length) {
      P = intersect(P, portfolioIdsForCampaigns(C, graph));
    }

    if (
      setsEqual(prevP, P) &&
      setsEqual(prevC, C) &&
      setsEqual(prevA, A) &&
      setsEqual(prevK, K)
    ) {
      break;
    }
  }

  // Empty child dimensions are valid (auto campaigns, incomplete catalog). Conflict
  // only when a pinned dimension (or the campaigns under a pinned portfolio) is empty.
  const conflict =
    (f.portfolioIds.length > 0 && (P.size === 0 || C.size === 0)) ||
    (f.campaignKeys.length > 0 && C.size === 0) ||
    (f.adGroupIds.length > 0 && A.size === 0) ||
    (f.keywordIds.length > 0 && K.size === 0);
  return { portfolioIds: P, campaignKeys: C, adGroupIds: A, keywordIds: K, conflict };
}
